import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Idea } from '@/lib/types';
import { GrowthIcon } from './GrowthIcon';
import { ProgressDots } from './ProgressDots';
import { getProgressDots, calculateDaysSinceWatered } from '@/lib/time-utils';
import { useIdeaUpdates } from '@/hooks/useIdeaUpdates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Droplets, Archive, Trash2, MessageSquare, Target, ListTodo, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface IdeaDetailSheetProps {
  idea: Idea | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: (id: string, updates: Partial<Idea>) => Promise<boolean>;
  onWater: () => void;
  onArchive: (id: string) => Promise<boolean>;
  onDelete: (id: string) => Promise<boolean>;
  onSetFocus: (id: string) => Promise<boolean>;
  onClearFocus: (id: string) => Promise<boolean>;
}

export function IdeaDetailSheet({
  idea,
  open,
  onOpenChange,
  onUpdate,
  onWater,
  onArchive,
  onDelete,
  onSetFocus,
  onClearFocus,
}: IdeaDetailSheetProps) {
  const navigate = useNavigate();
  const [editedFields, setEditedFields] = useState<Partial<Idea>>({});
  const { updates, loading: updatesLoading } = useIdeaUpdates(idea?.id || null, 3);

  if (!idea) return null;

  const isTree = idea.growth_stage === 'tree';
  const days = calculateDaysSinceWatered(idea);
  const dots = getProgressDots(idea);

  const handleFieldBlur = async (field: keyof Idea) => {
    if (editedFields[field] !== undefined && editedFields[field] !== idea[field]) {
      await onUpdate(idea.id, { [field]: editedFields[field] });
    }
    setEditedFields(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const getValue = (field: keyof Idea): string => {
    if (editedFields[field] !== undefined) {
      return editedFields[field] as string;
    }
    return (idea[field] as string) || '';
  };

  const handleChange = (field: keyof Idea, value: string) => {
    setEditedFields(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader className="pb-2">
          <div className="flex items-center gap-3">
            <GrowthIcon stage={idea.growth_stage} size={28} />
            <div className="flex-1">
              <SheetTitle className="text-left">
                {idea.title || 'Untitled Idea'}
              </SheetTitle>
              {!isTree && (
                <div className="flex items-center gap-3 mt-1">
                  <ProgressDots count={dots} />
                  <span className="text-xs text-muted-foreground">
                    {days} day{days !== 1 ? 's' : ''} since last watered
                  </span>
                </div>
              )}
            </div>
          </div>
        </SheetHeader>

        {/* Content */}
        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={getValue('title')}
                onChange={(e) => handleChange('title', e.target.value)}
                onBlur={() => handleFieldBlur('title')}
                placeholder="Give your idea a name..."
              />
            </div>

            {/* One-liner */}
            <div className="space-y-2">
              <Label htmlFor="one_liner">One-liner</Label>
              <Textarea
                id="one_liner"
                value={getValue('one_liner')}
                onChange={(e) => handleChange('one_liner', e.target.value)}
                onBlur={() => handleFieldBlur('one_liner')}
                placeholder="Summarize in one sentence..."
                className="resize-none"
                rows={2}
              />
            </div>

            <Separator />

            {/* Problem */}
            <div className="space-y-2">
              <Label htmlFor="problem">Problem</Label>
              <Textarea
                id="problem"
                value={getValue('problem')}
                onChange={(e) => handleChange('problem', e.target.value)}
                onBlur={() => handleFieldBlur('problem')}
                placeholder="What problem does this solve?"
                className="resize-none"
                rows={3}
              />
            </div>

            {/* Audience */}
            <div className="space-y-2">
              <Label htmlFor="audience">Audience</Label>
              <Textarea
                id="audience"
                value={getValue('audience')}
                onChange={(e) => handleChange('audience', e.target.value)}
                onBlur={() => handleFieldBlur('audience')}
                placeholder="Who is this for?"
                className="resize-none"
                rows={2}
              />
            </div>

            {/* Solution */}
            <div className="space-y-2">
              <Label htmlFor="proposed_solution">Proposed Solution</Label>
              <Textarea
                id="proposed_solution"
                value={getValue('proposed_solution')}
                onChange={(e) => handleChange('proposed_solution', e.target.value)}
                onBlur={() => handleFieldBlur('proposed_solution')}
                placeholder="How will you solve this?"
                className="resize-none"
                rows={3}
              />
            </div>

            {/* Differentiator */}
            <div className="space-y-2">
              <Label htmlFor="differentiator">Differentiator</Label>
              <Textarea
                id="differentiator"
                value={getValue('differentiator')}
                onChange={(e) => handleChange('differentiator', e.target.value)}
                onBlur={() => handleFieldBlur('differentiator')}
                placeholder="What makes this unique?"
                className="resize-none"
                rows={2}
              />
            </div>

            <Separator />

            {/* Recent Reflections */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <MessageSquare className="h-3.5 w-3.5" />
                Recent Reflections
              </Label>
              {updatesLoading ? (
                <p className="text-sm text-muted-foreground">Loading...</p>
              ) : updates.length === 0 ? (
                <p className="text-sm text-muted-foreground/60 italic">
                  No reflections yet. Water this idea to add your first.
                </p>
              ) : (
                <div className="space-y-3">
                  {updates.map((update) => (
                    <div 
                      key={update.id} 
                      className="bg-muted/30 rounded-lg p-3 space-y-1.5"
                    >
                      <p className="text-sm text-foreground/90 leading-relaxed">
                        {update.reflection}
                      </p>
                      {update.ai_summary && (
                        <p className="text-xs text-muted-foreground/70 italic border-l-2 border-primary/20 pl-2">
                          {update.ai_summary}
                        </p>
                      )}
                      <p className="text-[11px] text-muted-foreground/50">
                        {formatDistanceToNow(new Date(update.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            {/* Open Plan link */}
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={() => {
                onOpenChange(false);
                navigate(`/plan/${idea.id}`);
              }}
            >
              <ListTodo className="h-4 w-4" />
              Open Plan
              <ExternalLink className="h-3 w-3 ml-auto" />
            </Button>

            <Separator />

            {/* Tags */}
            {idea.keywords_tags && idea.keywords_tags.length > 0 && (
              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex flex-wrap gap-2">
                  {idea.keywords_tags.map((tag, i) => (
                    <Badge key={i} variant="secondary">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Raw Input */}
            <div className="space-y-2">
              <Label className="text-muted-foreground">Original Input</Label>
              <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                {idea.raw_input}
              </p>
            </div>
          </div>
        </ScrollArea>

        {/* Actions */}
        <div className="flex gap-2 pt-4 border-t mt-4">
          <Button onClick={onWater} className="flex-1">
            <Droplets className="mr-2 h-4 w-4" />
            {isTree ? 'Deepen' : 'Water'}
          </Button>
          <Button
            variant={idea.is_focus ? "default" : "outline"}
            size="icon"
            onClick={() => idea.is_focus ? onClearFocus(idea.id) : onSetFocus(idea.id)}
            title={idea.is_focus ? "Remove focus" : "Set as focus"}
          >
            <Target className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => onArchive(idea.id)}
          >
            <Archive className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => {
              if (confirm('Delete this idea permanently?')) {
                onDelete(idea.id);
                onOpenChange(false);
              }
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}